/**
 * Request batching for dashboards: GET calls made in the same moment (e.g. every chart on a page mounting)
 * are sent as one POST /api/v1/auth/batch and answered together. Same promise shape as axios.get:
 * resolves { data, status }, rejects { response: { status, data } }. A single call, or a batch that fails
 * (e.g. an API without the batch endpoint), falls back to normal requests. Registered as this.$batchGet.
 */
const WAIT_MS = 25;
const MAX_PER_BATCH = 30;
let queue = [];
let timer = null;
let batchUnavailable = false;

function toQuery(params) {
  const parts = [];
  Object.keys(params || {}).forEach((key) => {
    const value = params[key];
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      value.forEach((v) => parts.push(`${encodeURIComponent(key)}[]=${encodeURIComponent(v)}`));
    } else {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  });
  return parts.join("&");
}

function fullUrl(url, config) {
  let path = url.replace(/^https?:\/\/[^/]+/, "");
  if (!path.startsWith("/")) path = "/" + path;
  const query = toQuery(config && config.params);
  return query ? path + (path.includes("?") ? "&" : "?") + query : path;
}

function single(axios, item) {
  axios.get(item.url, item.config).then(item.resolve, item.reject);
}

function flush(axios) {
  timer = null;
  const items = queue;
  queue = [];
  for (let i = 0; i < items.length; i += MAX_PER_BATCH) {
    const chunk = items.slice(i, i + MAX_PER_BATCH);
    const batchable = chunk.filter((it) => it.path.startsWith("/api/v1/auth/"));
    chunk.filter((it) => !it.path.startsWith("/api/v1/auth/")).forEach((it) => single(axios, it));
    if (batchUnavailable || batchable.length < 2) {
      batchable.forEach((it) => single(axios, it));
      continue;
    }
    axios
      .post("/api/v1/auth/batch", { requests: batchable.map((it, idx) => ({ id: idx, url: it.path })) })
      .then((res) => {
        const answers = (res.data && res.data.responses) || [];
        batchable.forEach((it, idx) => {
          const answer = answers.find((a) => a.id === idx);
          if (!answer) return single(axios, it);
          if (answer.status >= 200 && answer.status < 300) it.resolve({ data: answer.body, status: answer.status });
          else it.reject({ response: { status: answer.status, data: answer.body } });
        });
      })
      .catch((error) => {
        if (error && error.response && error.response.status === 404) batchUnavailable = true;
        batchable.forEach((it) => single(axios, it));
      });
  }
}

export function batchGet(axios, url, config = {}) {
  return new Promise((resolve, reject) => {
    queue.push({ url, config, path: fullUrl(url, config), resolve, reject });
    if (!timer) timer = setTimeout(() => flush(axios), WAIT_MS);
  });
}

export function installBatchGet(Vue, axios) {
  Vue.prototype.$batchGet = (url, config) => batchGet(axios, url, config);
}
